import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import AssignEngineerModal from "./AssignEngineer";
import MarkdownRenderer from '../../components/MarkdownRenderer';
/* ------------------ Helper Components ------------------ */

const DividerLine = () => (
    <Box sx={{ borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`, my: 2 }} />
);

const STATUS_STEPS = [
    "Unassigned",
    "Assigned",
    "Visited",
    "Sign Pending",
    "Final Report",
];

// Status badge color mapping
const STATUS_COLORS = {
    "UNASSIGNED": "#F8D66D",
    "ASSIGNED": "#FFB54C",
    "VISITED": "#B3EBF2",
    "SIGNPENDING": "#A5E3C2",
    "FINALREPORT": "#8CD47E",
    "REJECTED": "#FF6961",
};

// Create a separate StatusProgress component
const StatusProgress = ({ currentStatus, userRole }) => {
  // Filter out "Sign Pending" for client role
  const getFilteredSteps = () => {
    if (userRole === "CLIENT") {
      return STATUS_STEPS.filter(step => step !== "Sign Pending");
    }
    return STATUS_STEPS;
  };

  const filteredSteps = getFilteredSteps();

  // Map status to active index for FILTERED steps
  const getActiveIndex = (status) => {
    if (status === "REJECTED") return -1;
    
    if (userRole === "CLIENT") {
      const clientStatusMap = {
        "UNASSIGNED": 0,
        "ASSIGNED": 1,
        "VISITED": 2,
        "SIGNPENDING": 2, // This shouldn't happen for CLIENT but just in case
        "FINALREPORT": 3
      };
      return clientStatusMap[status] || 0;
    } else {
      const otherStatusMap = {
        "UNASSIGNED": 0,
        "ASSIGNED": 1,
        "VISITED": 2,
        "SIGNPENDING": 3,
        "FINALREPORT": 4
      };
      return otherStatusMap[status] || 0;
    }
  };

  const ACTIVE_INDEX = getActiveIndex(currentStatus);
  const DOT_SIZE = 14;
  const DOT_BORDER = 2;
  const STEP_GAP = userRole === "CLIENT" ? 115 : 80;
  const DOT_CENTER = (DOT_SIZE + DOT_BORDER * 2) / 2;

  return (
    <Box sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
          position: "relative",
        }}
      >
        {filteredSteps.map((label, index) => {
          // Compare against index in filtered steps, not original steps
          const isActive = index <= ACTIVE_INDEX;
          const isCompleted = index < ACTIVE_INDEX;

          return (
            <Box
              key={label}
              sx={{
                width: STEP_GAP,
                textAlign: "center",
                position: "relative",
              }}
            >
              {/* DOT */}
              <Box
                sx={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: "50%",
                  bgcolor: isActive ? COLORS.SECONDARY : "#fff",
                  border: `${DOT_BORDER}px solid ${COLORS.SECONDARY}`,
                  mx: "auto",
                  position: "relative",
                  zIndex: 2,
                }}
              />

              {/* LINE TO NEXT DOT - Hide if next step is "Sign Pending" for client */}
              {index !== filteredSteps.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: DOT_CENTER - 1,
                    left: `calc(50% + ${DOT_SIZE / 2}px)`,
                    width: STEP_GAP + DOT_SIZE * 5,
                    height: 2,
                    bgcolor: isCompleted
                      ? COLORS.SECONDARY
                      : COLORS.LIGHT_SECONDARY,
                    zIndex: 1,
                  }}
                />
              )}

              {/* LABEL */}
              <Typography
                sx={{
                  ...TYPOGRAPHY.CAPTION_MICRO,
                  color: COLORS.PRIMARY,
                  mt: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ProjectMetaRow = ({ project, role, onAssignEngineer, onDownloadReport, onOpenDrawing, downloadingReport }) => {
    // Check if there are drawings available
    const hasDrawings = project?.specificInstructions?.ImageTrackList?.length > 0;
    const firstDrawing = hasDrawings ? project.specificInstructions.ImageTrackList[0] : null;

    // Check if action buttons are visible
    const hasAdminActionButton = (role === "ADMIN" || role === "SUPERADMIN") &&
                                  project?.status && ["UNASSIGNED", "ASSIGNED"].includes(project.status);
    
    const hasClientActionButton = (role === "ADMIN" || role === "SUPERADMIN" || role === "CLIENT" || role === "ENGINEER") && 
                                  project?.status === "FINALREPORT";
    


    // Fixed button width for consistency
    const buttonWidth = "180px";
    const buttonHeight = "40px";

    return (
        <>
            {/* Main Details Row */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr auto",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                {/* Site Checks */}
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>Site Checks:</Typography>
                    <Typography 
                        sx={{
                            ...TYPOGRAPHY.BODY_STANDARD,
                            color: COLORS.SECONDARY,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: "100%",
                        }}
                    >
                        {project?.templateTitle || "N/A"}
                    </Typography>
                </Box>

                {/* Additional Instructions */}
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT }}>
                        Additional Time / Instructions:
                    </Typography>
                    <Typography 
                        sx={{
                            ...TYPOGRAPHY.BODY_STANDARD,
                            color: COLORS.SECONDARY,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            maxWidth: "100%",
                        }}
                    >
                        {project?.additionalTime || "N/A"}
                    </Typography>
                </Box>

                {/* Buttons Container - Main Row */}
                <Box sx={{ 
                    display: "flex", 
                    justifyContent: "flex-end",
                    minWidth: buttonWidth,
                    height: buttonHeight,
                }}>
                    {/* For ADMIN/SUPERADMIN - Assign Engineer button */}
                    {hasAdminActionButton && (
                        <Button
                            onClick={onAssignEngineer}
                            startIcon={
                                project.status === "ASSIGNED" 
                                    ? <Icon name="edit" color="#fff" size={18} />
                                    : <Icon name="plus2" color="#fff" size={18} />
                            }
                            sx={{
                                width: buttonWidth,
                                height: buttonHeight,
                                bgcolor: project.status === "ASSIGNED" ? COLORS.PRIMARY : COLORS.SECONDARY,
                                color: "#fff",
                                borderRadius: 2,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                '&:hover': {
                                    bgcolor: project.status === "ASSIGNED" ? COLORS.PRIMARY_DARK : COLORS.SECONDARY_DARK,
                                }
                            }}
                        >
                            {project.status === "ASSIGNED" ? "Edit Engineer" : "Assign Engineer"}
                        </Button>
                    )}

                    {/* For CLIENT - Download Report button */}
                    {hasClientActionButton && (
                        <Button
                            onClick={onDownloadReport} 
                            startIcon={
                                downloadingReport ? 
                                    <CircularProgress size={16} color="inherit" /> : 
                                    <Icon name="download1" color="#fff" size={18} />
                            }
                            sx={{
                                width: buttonWidth,
                                height: buttonHeight,
                                bgcolor: COLORS.SECONDARY,
                                color: "#fff",
                                borderRadius: 2,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                '&:hover': {
                                    bgcolor: COLORS.SECONDARY_DARK,
                                }
                            }}
                        >
                            {downloadingReport ? "Downloading..." : "Download Report"}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Drawing Details Row - ALWAYS shows when drawings exist */}
            {hasDrawings && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 2fr auto",
                        alignItems: "center",
                        gap: 2,
                        mt: 1.5,
                    }}
                >
                    {/* Drawing Number - In FIRST column (under Site Checks) */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}>
                        <Typography
                            sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.TEXT,
                                mr: 0.5,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Drawing Number :
                        </Typography>
                        <Typography
                            sx={{
                                ...TYPOGRAPHY.BODY_STANDARD,
                                color: COLORS.PRIMARY,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                wordBreak: 'keep-all',
                            }}
                        >
                            {firstDrawing?.drawingNumber || "AKR-123"}
                        </Typography>
                    </Box>

                    {/* Empty middle column */}
                    <Box />

                    {/* View Drawing button in second row - ALWAYS SHOWS when there are drawings */}
                    <Box sx={{ 
                        display: "flex", 
                        justifyContent: "flex-end",
                        minWidth: buttonWidth,
                        height: buttonHeight,
                    }}>
                        <Button
                            onClick={() => onOpenDrawing(firstDrawing)}
                            startIcon={<Icon name="view1" color="#fff" size={18} />}
                            sx={{
                                width: buttonWidth,
                                height: buttonHeight,
                                bgcolor: COLORS.PRIMARY,
                                color: "#fff",
                                borderRadius: 2,
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                '&:hover': {
                                    bgcolor: COLORS.PRIMARY_DARK,
                                }
                            }}
                        >
                            View Drawing
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};

const InfoColumn = ({ title, data, showIcon = true }) => {
  const getIconName = () => {
    if (title.includes("Admin")) return "profile";
    if (title.includes("Engineer")) return "profile";
    if (title.includes("Client")) return "profile";
    if (title.includes("POC")) return "profile";
    return "profile";
  };

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        pl: 3,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 15,
          top: 20,
          bottom: -8,
          width: "2px",
          bgcolor: COLORS.LIGHT_SECONDARY,
        }}
      />

      <Typography
        sx={{
          color: COLORS.TEXT,
          ...TYPOGRAPHY.BODY_LARGE,
          mb: 1,
          ml: -1.8,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>

      <Box>
        {data?.name && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.8 }}>
            {showIcon && (
              <Icon
                name={getIconName()}
                color={COLORS.LIGHT_SECONDARY}
                size={18}
              />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.name}
            </Typography>
          </Box>
        )}

        {data?.email && data.email !== "-" && (
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.8 }}>
            {showIcon && (
              <Icon name="at" color={COLORS.LIGHT_SECONDARY} size={16} />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.email}
            </Typography>
          </Box>
        )}

        {data?.phone && data.phone !== "-" && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {showIcon && (
              <Icon name="phoneDone" color={COLORS.LIGHT_SECONDARY} size={16} />
            )}
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_STANDARD,
                ml: showIcon ? 1 : 0,
                color: COLORS.SECONDARY,
                wordBreak: "break-word",
              }}
            >
              {data.phone}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Updated ChecklistTable component with improved structure
const ChecklistTable = ({ checklistData, isTemplate = false }) => {
    if (!checklistData?.sections || checklistData.sections.length === 0) {
        return (
            <Typography sx={{ color: COLORS.TEXT, textAlign: 'center', py: 3 }}>
                No checklist data available
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            {/* Table Header */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 4fr) 60px 60px 80px minmax(0, 2fr)",
                    alignItems: "center",
                    pb: 1,
                    borderBottom: `2px solid ${COLORS.SECONDARY}`,
                }}
            >
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                    Checklist Item
                </Typography>
                <Typography align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>Yes</Typography>
                <Typography align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>No</Typography>
                <Typography align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>NA</Typography>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT,textAlign: 'center' }}>Remarks</Typography>
            </Box>

            {checklistData.sections.map((section, sectionIndex) => (
                <Box key={sectionIndex}>
                    {/* Section Title Row */}
                    {section.name && (
                        <Box sx={{ 
                            display: "grid",
                            gridTemplateColumns: "minmax(0, 4fr) 60px 60px 80px minmax(0, 2fr)",
                            alignItems: "center",
                            py: 1.5,
                            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            backgroundColor: COLORS.LIGHT_SECONDARY + '20',
                        }}>
                            <Typography
                                sx={{
                                    color: COLORS.PRIMARY,
                                    ...TYPOGRAPHY.BODY_LARGE,
                                    fontWeight: 600,
                                    pl: 2
                                }}
                            >
                                {section.name}
                            </Typography>
                            <Box></Box>
                            <Box></Box>
                            <Box></Box>
                            <Box></Box>
                        </Box>
                    )}

                    {/* Section Items */}
                    {section.items && section.items.map((item, itemIndex) => (
                        <Box
                            key={itemIndex}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 4fr) 60px 60px 80px minmax(0, 2fr)",
                                alignItems: "flex-start",
                                py: 1.5,
                                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            }}
                        >
                            {/* Checklist Item with Description and Criteria */}
                            <Box sx={{ pr: 2 }}>
                                <Typography
                                    sx={{
                                        color: COLORS.PRIMARY,
                                        ...TYPOGRAPHY.BODY_STANDARD,
                                        fontWeight: 600,
                                        mb: 0.5
                                    }}
                                >
                                    {item.srNo}. {item.question}
                                </Typography>
                                {item.testDescription && (
                                    <Typography
                                        sx={{
                                            color: COLORS.SECONDARY,
                                            ...TYPOGRAPHY.BODY_SMALL,
                                            fontStyle: 'italic',
                                            mb: 0.5
                                        }}
                                    >
                                        Test: {item.testDescription}
                                    </Typography>
                                )}
                                {item.acceptanceCriteria && (
                                    <Typography
                                        sx={{
                                            color: COLORS.SECONDARY,
                                            ...TYPOGRAPHY.BODY_SMALL,
                                            mb: 0.5
                                        }}
                                    >
                                        Criteria: {item.acceptanceCriteria}
                                    </Typography>
                                )}
                            </Box>

                            {/* Yes Checkbox - Show checkmark only if it's filled data */}
                            <Box sx={{ textAlign: "center" }}>
                                {!isTemplate && item.answer === "YES" && <Icon name="check2" color={COLORS.PRIMARY} size={20} />}
                                {isTemplate && <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 12 }}>--</Typography>}
                            </Box>

                            {/* No Checkbox */}
                            <Box sx={{ textAlign: "center" }}>
                                {!isTemplate && item.answer === "NO" && <Icon name="check2" color={COLORS.PRIMARY} size={20} />}
                                {isTemplate && <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 12 }}>--</Typography>}
                            </Box>

                            {/* NA Checkbox */}
                            <Box sx={{ textAlign: "center" }}>
                                {!isTemplate && item.answer === "NA" && <Icon name="check2" color={COLORS.PRIMARY} size={20} />}
                                {isTemplate && <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: 12 }}>--</Typography>}
                            </Box>

                            {/* Remarks */}
                            <Typography
    sx={{
        color: isTemplate ? COLORS.LIGHT_SECONDARY : COLORS.PRIMARY,
        whiteSpace: "pre-line",
        wordBreak: "break-word",
        textAlign: "center",                    // center alignment
        ...TYPOGRAPHY.BODY_STANDARD,
        fontStyle: isTemplate ? "italic" : "normal",

        ...(isTemplate && {
            border: "1px dashed",
            borderColor: COLORS.LIGHT_SECONDARY,
            borderRadius: "4px",
            padding: "6px 8px",
        }),
    }}
>
    {!isTemplate ? (item.remarks || "-") : "Will be filled during site visit"}
</Typography>

                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

// New component for Template Checklist View
const TemplateChecklistView = ({ templateData }) => {
    if (!templateData) {
        return (
            <Typography sx={{ color: COLORS.TEXT, textAlign: 'center', py: 3 }}>
                Loading template data...
            </Typography>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
                Checklist Template: {templateData.title}
            </Typography>
            {templateData.description && (
                <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY, mb: 2 }}>
                    {templateData.description}
                </Typography>
            )}
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT, mb: 2, fontStyle: 'italic' }}>
                This is the template that will be used during the site visit. Answers will be filled by the engineer.
            </Typography>
            <ChecklistTable checklistData={templateData} isTemplate={true} />
        </Box>
    );
};

// Helper function to get full image URL
const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
        return imagePath;
    }
    
    if (imagePath.startsWith('data:image')) {
        return imagePath;
    }
    
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    return `${url}/${cleanPath}`;
};

// Simple Photo Gallery Component
const SimplePhotoGallery = ({ photos }) => {
    if (!photos || photos.length === 0) {
        return (
            <Typography sx={{ color: COLORS.TEXT, textAlign: 'center', py: 2 }}>
                No photos uploaded
            </Typography>
        );
    }

    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
                Photos ({photos.length})
            </Typography>
            
            {/* Flex container for 2 images per row */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'space-between'
            }}>
                {photos.map((photo, index) => (
                    <Box 
                        key={index}
                        sx={{
                            flex: '0 0 calc(50% - 8px)', // Exactly 50% width minus half the gap
                            mb: 2
                        }}
                    >
                        {/* Image */}
                        <Box
                            component="img"
                            src={getFullImageUrl(photo.image)}
                            alt={`Checklist photo ${index + 1}`}
                            sx={{
                                width: "100%",
                                height: 240,
                                objectFit: "contain",
                                borderRadius: 1,
                                border: `2px solid ${COLORS.LIGHT_SECONDARY}`,
                                display: 'block'
                            }}
                        />
                        
                        {/* Description */}
                        {photo.description && (
                            <Typography
                                sx={{
                                    ...TYPOGRAPHY.BODY_SMALL,
                                    color: COLORS.TEXT,
                                    mt: 0.5,
                                    wordBreak: 'break-word'
                                }}
                            >
                                {photo.description}
                            </Typography>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Drawing Viewer Modal Component - FULL SCREEN
const DrawingViewerModal = ({ open, onClose, drawing }) => {
    if (!drawing) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    m: 0,
                    height: '100vh',
                    width: '100vw',
                    maxHeight: '100vh',
                    maxWidth: '100vw'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                pb: 1,
                bgcolor: '#fff',
                position: 'sticky',
                top: 0,
                zIndex: 1
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Drawing {drawing.drawingNumber || "1"}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Icon name="close" color={COLORS.TEXT} />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ 
                p: 0, 
                display: 'flex', 
                flexDirection: 'column',
                height: 'calc(100vh - 64px)',
                bgcolor: '#f5f5f5'
            }}>
                {drawing.fullUrl && (
                    <Box sx={{ 
                        flex: 1, 
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%'
                    }}>
                        {/* PDF Viewer */}
                        {drawing.fullUrl.toLowerCase().endsWith('.pdf') ? (
                            <Box sx={{ 
                                flex: 1,
                                height: '100%',
                                width: '100%',
                                border: 'none'
                            }}>
                                <iframe
                                    src={`${drawing.fullUrl}#view=FitH`}
                                    title={`Drawing ${drawing.drawingNumber}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                />
                            </Box>
                        ) : (
                            /* Image Viewer for other formats */
                            <Box sx={{ 
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'auto',
                                p: 2,
                                width: '100%'
                            }}>
                                <Box
                                    component="img"
                                    src={drawing.fullUrl}
                                    alt={`Drawing ${drawing.drawingNumber}`}
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions sx={{ 
                borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                p: 1.5,
                bgcolor: '#fff',
                position: 'sticky',
                bottom: 0,
                zIndex: 1
            }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: COLORS.PRIMARY,
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    Close
                </Button>
                <Button
                    onClick={() => window.open(drawing.fullUrl, '_blank')}
                    startIcon={<Icon name="external" size={16} />}
                    sx={{
                        bgcolor: COLORS.PRIMARY,
                        color: '#fff',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                            bgcolor: '#1976d2'
                        }
                    }}
                >
                    Open in New Tab
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/* ------------------ Main Component ------------------ */

const ClientDetailsSidebar = ({ open, onClose, projectId }) => {
    const { token, hasRole } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [project, setProject] = useState(null);
    const [checklistData, setChecklistData] = useState(null);
    const [checklistTemplateData, setChecklistTemplateData] = useState(null); // New state for template data
    const [clientDetails, setClientDetails] = useState(null);
    const [adminDetails, setAdminDetails] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(false);
    // Add drawing modal state
    const [drawingModalOpen, setDrawingModalOpen] = useState(false);
    const [selectedDrawing, setSelectedDrawing] = useState(null);
    // Add this state with your other states
    const [generalContent, setGeneralContent] = useState({
        thankYou: null,
        termsConditions: null
    });

    // Get user role
    const getUserRole = () => {
        if (hasRole("CLIENT")) return "CLIENT";
        if (hasRole("ADMIN")) return "ADMIN";
        if (hasRole("SUPERADMIN")) return "SUPERADMIN";
        if (hasRole("ENGINEER")) return "ENGINEER";
        return null;
    };

    const resetStates = () => {
        setLoading(true);
        setError(null);
        setProject(null);
        setChecklistData(null);
        setChecklistTemplateData(null); // Reset template data
        setClientDetails(null);
        setAdminDetails(null);
        setGeneralContent({
            thankYou: null,
            termsConditions: null
        });
        setAssignModalOpen(false);
        setDrawingModalOpen(false);
        setSelectedDrawing(null);
    };

    // Update your useEffect
    useEffect(() => {
        if (open && projectId) {
            resetStates(); // Reset all states first
            fetchProjectAndRelatedData();
            fetchGeneralContent();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, projectId]);

    // Add this function with your other fetch functions
    const fetchGeneralContent = async () => {
        try {
            // Fetch both Thank You and Terms & Conditions
            const [thankYouResponse, termsResponse] = await Promise.all([
                axios.get(`${url}/api/general/content/THANK_YOU`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: { data: null } })),
                
                axios.get(`${url}/api/general/content/TERMS_CONDITIONS`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => ({ data: { data: [] } }))
            ]);

            // Process Thank You content
            const thankYouData = thankYouResponse.data?.data;
            const termsData = termsResponse.data?.data;

            console.log("Fetched general content:", {
                thankYou: thankYouData,
                termsConditions: termsData
            });

            setGeneralContent({
                thankYou: Array.isArray(thankYouData) ? thankYouData[0] : thankYouData,
                termsConditions: Array.isArray(termsData) ? termsData[0] : termsData
            });
        } catch (err) {
            console.error("Error fetching general content:", err);
            // Don't set error state, as this is not critical
        }
    };




  const fetchProjectAndRelatedData = async () => {
    try {
        setLoading(true);
        setError(null);
        console.log("Fetching project details for ID:", projectId);

        // 1. Fetch project details
        let projectData = null;
        try {
            const projectResponse = await axios.get(`${url}/api/client/requests`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { requestID: projectId }
            });
            if (projectResponse.data?.requests?.length > 0) {
                projectData = projectResponse.data.requests[0];
            }
        } catch (e) {
            console.log("Not found in /api/client/requests, trying fallback APIs...");
        }

        if (!projectData) {
            try {
                const singleRes = await axios.get(`${url}/api/projects/get/${projectId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (singleRes.data?.success && singleRes.data?.data) {
                    projectData = singleRes.data.data;
                }
            } catch (e) {
                console.log("Not found in /api/projects/get");
            }
        }

        if (!projectData) {
            try {
                const firRes = await axios.get(`${url}/api/fir/requests`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (firRes.data?.success && firRes.data?.data) {
                    projectData = firRes.data.data.find(r => r._id === projectId || r.originalFirId === projectId);
                }
            } catch (e) {
                console.log("Not found in /api/fir/requests");
            }
        }

        if (projectData) {
            console.log("Project data loaded:", projectData);
            console.log("Project status:", projectData.status);
            console.log("Template ID field:", projectData.templateID);
            console.log("Checklist ID field:", projectData.checklistId);
            setProject(projectData);

            // 2. If status is VISITED or beyond, fetch checklist data from engineer API
            if (["VISITED", "SIGNPENDING", "FINALREPORT"].includes(projectData.status)) {
                console.log("Fetching filled checklist data...");
                await fetchChecklistData(projectData._id);
            } else {
                // 3. If status is UNASSIGNED or ASSIGNED, fetch template data
                // Check both possible field names
                const templateIdToFetch = projectData.templateID || projectData.templateId || projectData.checklistId;
                console.log("Template ID to fetch:", templateIdToFetch);
                
                if (templateIdToFetch) {
                    console.log("Fetching template data with ID:", templateIdToFetch);
                    await fetchChecklistTemplate(templateIdToFetch);
                } else {
                    console.warn("No templateID, templateId, or checklistId found in project data");
                    console.warn("Available project fields:", Object.keys(projectData));
                }
            }

            // 4. Fetch client details
            if (projectData.clientID) {
                await fetchUserDetails(projectData.clientID, "client");
            } else {
                setClientDetails({
                    name: projectData.nameOfClient || "N/A",
                    email: "-",
                    phone: projectData.phoneOfClient || "-"
                });
            }

            // 5. Fetch admin details 
            await fetchAdminDetails();
        } else {
            setError("Project not found");
        }
    } catch (err) {
        console.error("Error fetching project details:", err);
        setError(err.response?.data?.message || "Failed to load project details");
    } finally {
        console.log("Finished loading project data");
        setLoading(false);
    }
};

    // Fetch checklist data from engineer API when status is VISITED or beyond
    const fetchChecklistData = async (requestId) => {
        try {
            console.log("Fetching checklist data for request ID:", requestId);
            const response = await axios.get(`${url}/api/engineer/open-checklist/${requestId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log("Checklist API response:", response.data);

            if (response.data?.data) {
                const checklist = response.data.data;
                
                console.log("Other observations from checklist API:", checklist.otherObservations);
                console.log("Photos from checklist:", checklist.photos);
                
                // Process images to ensure they have full URLs
                if (checklist.photos) {
                    checklist.photos = checklist.photos.map(photo => ({
                        ...photo,
                        image: getFullImageUrl(photo.image)
                    }));
                }
                
                // Process signatures to ensure they have full URLs
                if (checklist.signatures) {
                    ['engineer', 'clientSiteEngineer', 'admin'].forEach(role => {
                        if (checklist.signatures[role]?.signature) {
                            checklist.signatures[role].signature = getFullImageUrl(checklist.signatures[role].signature);
                        }
                    });
                }
                
                setChecklistData(checklist);
            }
        } catch (err) {
            console.error("Error fetching checklist data:", err);
            // Don't set error here, as checklist might not exist yet
        }
    };

    // New function to fetch checklist template data
// New function to fetch checklist template data
const fetchChecklistTemplate = async (templateId) => {
    try {
        console.log("Fetching checklist template for ID:", templateId);
        console.log("API URL:", `${url}/api/admin/checklist-templates/${templateId}`);
        
        const response = await axios.get(`${url}/api/admin/checklist-templates/${templateId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        console.log("Checklist template API response status:", response.status);
        console.log("Checklist template API response data:", response.data);

        if (response.data?.data) {
            const template = response.data.data;
            console.log("Template data received:", template);
            console.log("Template title:", template.title);
            console.log("Template sections length:", template.sections?.length);
            console.log("Template sections:", template.sections);
            
            setChecklistTemplateData(template);
        } else {
            console.warn("No data in template response or unexpected structure");
            console.warn("Response structure:", response.data);
        }
    } catch (err) {
        console.error("Error fetching checklist template:", err);
        console.error("Error response:", err.response?.data);
        console.error("Error status:", err.response?.status);
        // Don't set error here, it's not critical
    }
};                 

    const fetchUserDetails = async (userId, type) => {
        try {
            const response = await axios.get(`${url}/api/users/fetch`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { userID: userId }
            });

            if (response.data?.users?.length > 0) {
                const user = response.data.users[0];
                if (type === "client") {
                    setClientDetails({
                        name: user.name || user.companyName || "N/A",
                        email: user.email || "-",
                        phone: user.phone || "-"
                    });
                }
            }
        } catch (err) {
            console.error(`Error fetching ${type} details:`, err);
            if (type === "client" && project) {
                setClientDetails({
                    name: project.nameOfClient || "N/A",
                    email: "-",
                    phone: project.phoneOfClient || "-"
                });
            }
        }
    };

  const fetchAdminDetails = async () => {
  try {
    const response = await axios.get(`${url}/api/users/fetch`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { 
        role: "ADMIN", 
        status: "APPROVED", 
        limit: 1 
      },
    });

    if (response.data?.users?.length > 0) {
      const admin = response.data.users[0];
      
      // Use company details if available, otherwise fall back to admin details
      setAdminDetails({
        name: admin.name || "Admin",
        // Use company email and phone instead of admin's personal ones
        email: admin.companyDetails?.email || admin.email || "-",
        phone: admin.companyDetails?.phone || admin.phone || "-",
        // Store full company details if needed elsewhere
        companyDetails: admin.companyDetails || null,
      });
    } else {
      // Default values when no admin found
      setAdminDetails({
        name: "Admin",
        email: "-",
        phone: "-",
        companyDetails: null,
      });
    }
  } catch (err) {
    console.error("Error fetching admin details:", err);
    setAdminDetails({
      name: "Admin",
      email: "-",
      phone: "-",
      companyDetails: null,
    });
  }
};



    const formatDateTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/');
        const timeStr = date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit'
        });
        return `${dateStr} | ${timeStr}`;
    };

    // Get POC details from project data
    const getPOCDetails = () => {
        return {
            name: project?.nameOfClient || "Point of Contact Person",
            email: project?.pocEmail || "-",
            phone: project?.phoneOfClient || "-"
        };
    };

    // Get engineer details from project data
    const getEngineerDetails = () => {
        if (!project?.assignedEngineer) {
            return {
                name: "Not Assigned",
                email: "-",
                phone: "-"
            };
        }

        if (typeof project.assignedEngineer === 'object') {
            return {
                name: project.assignedEngineer.name || "Engineer",
                email: project.assignedEngineer.email || "-",
                phone: project.assignedEngineer.phone || "-"
            };
        }

        return {
            name: "Engineer",
            email: "-",
            phone: "-"
        };
    };

    // Get third column info based on role and status
  const getThirdColumnInfo = () => {
  const userRole = getUserRole();
  const projectStatus = project?.status;

  if (projectStatus === "UNASSIGNED") {
    return null;
  }

  if (
    userRole === "CLIENT" &&
    projectStatus &&
    projectStatus !== "UNASSIGNED"
  ) {
    const engineerDetails = getEngineerDetails();
    return {
      title: "Assigned Engineer",
      data: {
        name: engineerDetails.name,
        // Use company email and phone from adminDetails
        email: adminDetails?.companyDetails?.email || adminDetails?.email || "-",
        phone: adminDetails?.companyDetails?.phone || adminDetails?.phone || "-",
      },
    };
  }

  if (["ADMIN", "SUPERADMIN", "ENGINEER"].includes(userRole)) {
    const engineerDetails = getEngineerDetails();
    return {
      title: "Assigned Engineer",
      data: {
        name: engineerDetails.name,
        // Use company email and phone for admin/engineer view too
        email: adminDetails?.companyDetails?.email || adminDetails?.email || engineerDetails.email || "-",
        phone: adminDetails?.companyDetails?.phone || adminDetails?.phone || engineerDetails.phone || "-",
      },
    };
  }

  return null;
};

    // Handle assign engineer
    const handleAssignEngineer = () => {
        setAssignModalOpen(true);
    };

    // Handle download report
  // Handle download report - Same logic as AllProjects component
const handleDownloadReport = async () => {
    try {
                setDownloadingReport(true);

        if (!token) {
            alert("Please login to download reports");
            return;
        }

        // Get user role from auth context
        const userRole = getUserRole(); // Use your existing getUserRole function
        console.log("Downloading as role:", userRole);
        
        // Make API call to download PDF with role as query parameter
        const response = await axios.get(
            `${url}/api/admin/download-checklist-report/${projectId}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    userRole: userRole  // Pass role as query parameter
                },
                responseType: 'blob',
            }
        );

        // Debug: Check headers
        console.log("Response headers:", response.headers);
        console.log("Content-Disposition header:", response.headers['content-disposition']);
        console.log("All headers:", JSON.stringify(response.headers, null, 2));

        // Create a blob from the PDF stream
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        
        // Create download link
        const downloadUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        // Try to get filename from headers
        const contentDisposition = response.headers['content-disposition'];
        console.log("Raw Content-Disposition:", contentDisposition);
        
        if (contentDisposition) {
            // Try different parsing methods
            let filename = null;
            
            // Method 1: Standard format
            const match = contentDisposition.match(/filename="([^"]+)"/i);
            if (match && match[1]) {
                filename = match[1];
            }
            
            // Method 2: Without quotes
            if (!filename) {
                const match2 = contentDisposition.match(/filename=([^;]+)/i);
                if (match2 && match2[1]) {
                    filename = match2[1].trim();
                }
            }
            
            console.log("Extracted filename:", filename);
            
            if (filename) {
                link.download = filename;
            } else {
                link.download = `checklist-report-${projectId}.pdf`;
            }
        } else {
            link.download = `checklist-report-${projectId}.pdf`;
        }
        
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        console.log("Report downloaded successfully");
        
    } catch (err) {
        console.error("Error downloading report:", err);
        console.error("Error response:", err.response);
        
        // Show error message
        let errorMsg = "Failed to download report";
        if (err.response?.status === 404) {
            errorMsg = "Report not found for this project";
        } else if (err.response?.status === 400) {
            errorMsg = "Report is not ready for download";
        } else if (err.response?.data?.message) {
            errorMsg = err.response.data.message;
        }
        
        alert(errorMsg);
    }
    finally {
                setDownloadingReport(false);

    }
};

    // Function to handle opening drawing
    const handleOpenDrawing = (drawingData) => {
        if (!drawingData?.drawing) {
            setError("No drawing available");
            return;
        }
        
        // Get full URL for the drawing
        const fullDrawingUrl = getFullImageUrl(drawingData.drawing);
        setSelectedDrawing({
            ...drawingData,
            fullUrl: fullDrawingUrl
        });
        setDrawingModalOpen(true);
    };

    // Function to handle closing drawing modal
    const handleCloseDrawing = () => {
        setDrawingModalOpen(false);
        setSelectedDrawing(null);
    };

    // Show signatures section based on checklist data
    const renderSignatures = () => {
        if (!checklistData?.signatures) return null;

        const { engineer, clientSiteEngineer, admin } = checklistData.signatures;

        return (
            <Box sx={{ mt: 4 }}>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 3 }}>
                    Signatures
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 4,
                        textAlign: "center",
                    }}
                >
                    {/* Engineer Signature */}
                    <Box>
                        {engineer?.signature ? (
                            <Box sx={{ 
                                height: 80, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Box
                                    component="img"
                                    src={getFullImageUrl(engineer.signature)}
                                    alt="Engineer Signature"
                                    sx={{
                                        maxHeight: '100%',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                height: 80, 
                                borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`, 
                                display: 'flex', 
                                alignItems: 'flex-end', 
                                justifyContent: 'center' 
                            }} />
                        )}
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_SMALL, 
                            color: COLORS.TEXT, 
                            mt: 1
                        }}>
                            PMC Engineer
                        </Typography>
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_STANDARD, 
                            color: COLORS.PRIMARY, 
                            mt: 0.5, 
                            fontWeight: 500
                        }}>
                            {engineer?.name || "Not Signed"}
                        </Typography>
                        {engineer?.signedAt && (
                            <Typography sx={{
                                ...TYPOGRAPHY.CAPTION, 
                                color: COLORS.SECONDARY, 
                                mt: 0.5
                            }}>
                                {formatDateTime(engineer.signedAt)}
                            </Typography>
                        )}
                    </Box>

                    {/* Client Site Engineer Signature */}
                    <Box>
                        {clientSiteEngineer?.signature ? (
                            <Box sx={{ 
                                height: 80, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Box
                                    component="img"
                                    src={getFullImageUrl(clientSiteEngineer.signature)}
                                    alt="Client Site Engineer Signature"
                                    sx={{
                                        maxHeight: '100%',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                height: 80, 
                                borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`, 
                                display: 'flex', 
                                alignItems: 'flex-end', 
                                justifyContent: 'center' 
                            }} />
                        )}
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_SMALL, 
                            color: COLORS.TEXT, 
                            mt: 1
                        }}>
                        Site Engineer
                        </Typography>
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_STANDARD, 
                            color: COLORS.PRIMARY, 
                            mt: 0.5, 
                            fontWeight: 500
                        }}>
                            {clientSiteEngineer?.name || "Not Signed"}
                        </Typography>
                        {clientSiteEngineer?.signedAt && (
                            <Typography sx={{
                                ...TYPOGRAPHY.CAPTION, 
                                color: COLORS.SECONDARY, 
                                mt: 0.5
                            }}>
                                {formatDateTime(clientSiteEngineer.signedAt)}
                            </Typography>
                        )}
                    </Box>

                    {/* Admin Signature */}
                    <Box>
                        {admin?.signature ? (
                            <Box sx={{ 
                                height: 80, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center' 
                            }}>
                                <Box
                                    component="img"
                                    src={getFullImageUrl(admin.signature)}
                                    alt="Admin Signature"
                                    sx={{
                                        maxHeight: '100%',
                                        maxWidth: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                height: 80, 
                                borderBottom: `2px solid ${COLORS.LIGHT_SECONDARY}`, 
                                display: 'flex', 
                                alignItems: 'flex-end', 
                                justifyContent: 'center' 
                            }} />
                        )}
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_SMALL, 
                            color: COLORS.TEXT, 
                            mt: 1
                        }}>
                            PMC
                        </Typography>
                        <Typography sx={{
                            ...TYPOGRAPHY.BODY_STANDARD, 
                            color: COLORS.PRIMARY, 
                            mt: 0.5, 
                            fontWeight: 500
                        }}>
                            {admin?.name || "Not Signed"}
                        </Typography>
                        {admin?.signedAt && (
                            <Typography sx={{
                                ...TYPOGRAPHY.CAPTION, 
                                color: COLORS.SECONDARY, 
                                mt: 0.5
                            }}>
                                {formatDateTime(admin.signedAt)}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    // Function to render other observations from checklist data
    const renderOtherObservations = () => {
        if (!checklistData?.otherObservations || checklistData.otherObservations.length === 0) {
            return null;
        }

        console.log("Rendering other observations:", checklistData.otherObservations);

        return (
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000" }}>
                    Other Observations 
                </Typography>
                <Box sx={{ pl: "20px", mt: 1 }}>
                    {checklistData.otherObservations.map((obs, idx) => {
                        // Handle both object and string formats
                        const observationText = typeof obs === 'object' 
                            ? obs.observation || obs.text || JSON.stringify(obs) 
                            : obs;
                        
                        console.log(`Observation ${idx}:`, obs, "Text:", observationText);
                        
                        return (
                            <Typography
                                key={idx}
                                sx={{
                                    ...TYPOGRAPHY.BODY_STANDARD,
                                    color: COLORS.PRIMARY,
                                    mb: 0.5,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {idx + 1}. {observationText}
                            </Typography>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // Function to render admin remarks from project data
    const renderAdminRemarks = () => {
        if (!project?.adminRemarks || project.adminRemarks.length === 0) {
            return null;
        }

        console.log("Rendering admin remarks:", project.adminRemarks);

        return (
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000" }}>
                    Admin Remarks on Project
                </Typography>
                <Box sx={{ pl: 2, mt: 1 }}>
                    {project.adminRemarks.map((point, index) => {
                        // Handle both string and object formats
                        const remarkText = typeof point === 'string' 
                            ? point 
                            : point.remark || point.text || point.observation || JSON.stringify(point);
                        
                        console.log(`Admin remark ${index}:`, point, "Text:", remarkText);
                        
                        return (
                            <Typography
                                key={index}
                                sx={{ 
                                    ...TYPOGRAPHY.BODY_STANDARD, 
                                    color: COLORS.PRIMARY,
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal'
                                }}
                            >
                                {index + 1}. {remarkText}
                            </Typography>
                        );
                    })}
                </Box>
            </Box>
        );
    };

        const formatDateRange = (dateRange) => {
        if (!dateRange?.fromDate) return "N/A";
        
        const fromDate = new Date(dateRange.fromDate);
        const toDate = dateRange.toDate ? new Date(dateRange.toDate) : null;
        
        const format = (date) => {
            return date.toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}
        
        if (toDate && fromDate.toDateString() !== toDate.toDateString()) {
            return `${format(fromDate)} - ${format(toDate)}`;
        }
        return format(fromDate);
    };

    // Function to render checklist section based on project status// Function to render checklist section based on project status
// Function to render checklist section based on project status
const renderChecklistSection = () => {
    const projectStatus = project?.status;
    console.log("renderChecklistSection called:", {
        projectStatus,
        hasChecklistData: !!checklistData,
        hasTemplateData: !!checklistTemplateData,
        loading,
        projectId: project?._id,
        templateID: project?.templateID, // Log the templateID field
        templateId: project?.templateId, // Log alternative field name
        checklistId: project?.checklistId // Log checklistId field
    });

    // If status is VISITED or beyond, show filled checklist data
    if (["VISITED", "SIGNPENDING", "FINALREPORT"].includes(projectStatus)) {
        console.log("Should show filled checklist data");
        if (checklistData) {
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
                        Checklist: {checklistData.title}
                    </Typography>
                    {checklistData.description && (
                        <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY, mb: 2 }}>
                            {checklistData.description}
                        </Typography>
                    )}
                    <ChecklistTable checklistData={checklistData} isTemplate={false} />
                </Box>
            );
        } else {
            console.log("No checklist data available for visited status");
            return null;
        }
    }
    
    // If status is UNASSIGNED or ASSIGNED, show template data
    if (["UNASSIGNED", "ASSIGNED"].includes(projectStatus)) {
        console.log("Should show template data for unassigned/assigned status");
        console.log("Template data available:", !!checklistTemplateData);
        
        if (checklistTemplateData) {
            console.log("Template data structure:", checklistTemplateData);
            return <TemplateChecklistView templateData={checklistTemplateData} />;
        } else {
            console.log("No template data available yet");
            console.log("Possible template ID fields:", {
                templateID: project?.templateID,
                templateId: project?.templateId,
                checklistId: project?.checklistId
            });
            
            if (loading) {
                return (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                        <Typography sx={{ color: COLORS.TEXT, mt: 1 }}>
                            Loading checklist template...
                        </Typography>
                    </Box>
                );
            } else {
                return (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ color: COLORS.TEXT, textAlign: 'center', py: 3 }}>
                            Checklist template will be displayed here once loaded
                        </Typography>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY, textAlign: 'center' }}>
                            Template ID: {project?.templateID || project?.templateId || project?.checklistId || "Not found"}
                        </Typography>
                    </Box>
                );
            }
        }
    }

    console.log("No checklist section to display - status:", projectStatus);
    return null;
};

    if (loading) {
        return (
            <Box
                sx={{
                    width: 800,
                    height: "100vh",
                    position: "fixed",
                    top: 0,
                    right: open ? 0 : "-100%",
                    bgcolor: "#fff",
                    zIndex: 1300,
                    transition: "right 0.3s ease",
                    boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
                    p: 3,
                    overflowY: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const thirdColumnInfo = getThirdColumnInfo();
    const userRole = getUserRole();

    return (
        <>
            {open && (
                <Box
                    onClick={onClose}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        zIndex: 1299,
                    }}
                />
            )}

            <Box
                sx={{
                    width: 800,
                    height: "100vh",
                    position: "fixed",
                    top: 0,
                    right: open ? 0 : "-100%",
                    bgcolor: "#fff",
                    zIndex: 1300,
                    transition: "right 0.3s ease",
                    boxShadow: "-4px 0 12px rgba(0,0,0,0.1)",
                    p: 3,
                    overflowY: "auto",
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
                        Site Check Project Details
                    </Typography>
                    <IconButton onClick={onClose} sx={{ p: 0.5 }}>
                        <Icon name="close" color={COLORS.ERROR} />
                    </IconButton>
                </Box>

                {error ? (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                ) : !project ? (
                    <Typography sx={{ color: COLORS.TEXT, textAlign: 'center' }}>
                        No project data available
                    </Typography>
                ) : (
                    <>
                        {/* Project Info */}
                        <Box sx={{ mb: 2 }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr auto",
                                    rowGap: 1,
                                    alignItems: "center",
                                }}
                            >
                                {/* LEFT – Project Name */}
                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', gap: 1 }}>
                                    <Icon name="block" color={COLORS.PRIMARY} size={20} />
                                    <Typography sx={{...TYPOGRAPHY.H3, wordBreak: 'break-word'}}>
                                        {project.nameOfProject}
                                    </Typography>
                                    <Chip
                                        label={project.status}
                                        size="small"
                                        sx={{
                                            ml: 2,
                                            bgcolor: STATUS_COLORS[project.status] || "#7ED957",
                                            color: "#fff",
                                            ...TYPOGRAPHY.BODY_SMALL
                                        }}
                                    />
                                </Box>

                                {/* RIGHT – Entry On */}
                             <Typography
            sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                color: COLORS.TEXT,
                textAlign: "right",
                whiteSpace: 'nowrap'
            }}
        >
            Preferred Date: {formatDateRange(project?.preferredSiteVisitDate)}
        </Typography>

                                {/* LEFT – Address */}
                                <Typography
                                    sx={{
                                        ...TYPOGRAPHY.BODY_STANDARD,
                                        color: COLORS.SECONDARY,
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {project.detailedAddress}
                                </Typography>

                                {/* RIGHT – Dynamic Date Display */}
                                {project.status === "ASSIGNED" && project.assignedVisitDate ? (
                                    <Typography
                                        sx={{
                                            ...TYPOGRAPHY.BODY_LARGE,
                                            color: COLORS.SECONDARY,
                                            textAlign: "right",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Visit Scheduled
                                        <Icon name="calendar3" />
                                        {formatDateTime(project.assignedVisitDate)}
                                    </Typography>
                                ) : (project.status === "VISITED" || project.status === "SIGNPENDING" || project.status === "FINALREPORT") && project.assignedVisitDate ? (
                                    <Typography
                                        sx={{
                                            ...TYPOGRAPHY.BODY_LARGE,
                                            color: COLORS.SECONDARY,
                                            textAlign: "right",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                            gap: 0.5,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {project.status === "VISITED" ? "Visited on" : 
                                        project.status === "SIGNPENDING" ? "Visited on" : 
                                        "Visited on"}
                                        <Icon name="calendar3" />
                                        {formatDateTime(project.assignedVisitDate)}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>

                        <DividerLine />

                        {/* Status Progress - Pass userRole as prop */}
                        <StatusProgress 
                            currentStatus={project.status} 
                            userRole={userRole}
                        />

                        {/* Project Meta Row with buttons */}
                        <ProjectMetaRow 
                            project={project} 
                            role={userRole}
                            onAssignEngineer={handleAssignEngineer}
                            onDownloadReport={handleDownloadReport}
                            onOpenDrawing={handleOpenDrawing}
                            downloadingReport={downloadingReport}
                        />

                        {/* NEW: Field Inspection Summary Section */}
                        {project.fieldInspectionSummary && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: "#F9FAFB", borderRadius: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                                <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Icon name="list" size={18} color={COLORS.PRIMARY} />
                                    Field Inspection Summary
                                </Typography>
                                
                                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, mb: 0.5 }}>Reports Overview</Typography>
                                        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 700 }}>
                                            {project.fieldInspectionSummary.totalReports || 0} <span style={{ fontWeight: 400, fontSize: '0.8rem', color: COLORS.SECONDARY }}>Total</span>
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.75rem", color: "#2E7D32" }}>
                                            {project.fieldInspectionSummary.closedReports || 0} Closed
                                        </Typography>
                                    </Box>
                                    
                                    <Box>
                                        <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, mb: 0.5 }}>Aggregated Responses</Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Chip 
                                                label={`YES: ${project.fieldInspectionSummary.totalYes || 0}`} 
                                                sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 600, height: 26 }} 
                                            />
                                            <Chip 
                                                label={`NO: ${project.fieldInspectionSummary.totalNo || 0}`} 
                                                sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600, height: 26 }} 
                                            />
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, mb: 0.5 }}>Critical Status</Typography>
                                        <Typography sx={{ 
                                            ...TYPOGRAPHY.BODY_LARGE, 
                                            color: (project.fieldInspectionSummary.criticalReports > 0 || project.fieldInspectionSummary.totalCriticalCount > 0) ? COLORS.ERROR : COLORS.SECONDARY, 
                                            fontWeight: 700 
                                        }}>
                                            {project.fieldInspectionSummary.criticalReports || 0} <span style={{ fontWeight: 400, fontSize: '0.8rem' }}>Critical {project.fieldInspectionSummary.criticalReports === 1 ? 'Report' : 'Reports'}</span>
                                        </Typography>
                                        {project.fieldInspectionSummary.totalCriticalCount > 0 && (
                                            <Typography sx={{ fontSize: "0.7rem", color: COLORS.ERROR }}>
                                                {project.fieldInspectionSummary.totalCriticalCount} total critical points
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        <DividerLine />

                        {/* Client / POC / Engineer or Admin */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: thirdColumnInfo ? "space-between" : "flex-start",
                                mt: 3,
                                mb: 3,
                                gap: 2
                            }}
                        >
                            {/* Client Details Column - From user API */}
                            <InfoColumn 
                                title="Client Details" 
                                data={clientDetails || {
                                    name: project?.nameOfClient || "N/A",
                                    email: "-",
                                    phone: project?.phoneOfClient || "-"
                                }} 
                            />
                            
                            {/* POC Column - From project data (nameOfClient, phoneOfClient) */}
                            <InfoColumn 
                                title="POC at Project" 
                                data={getPOCDetails()} 
                            />
                            
                            {/* Third Column - Conditionally show based on role and status */}
                            {thirdColumnInfo && (
                                <InfoColumn 
                                    title={thirdColumnInfo.title} 
                                    data={thirdColumnInfo.data} 
                                />
                            )}
                        </Box>

                        <DividerLine />

                        {/* Specific Instructions */}
                       {project.specificInstructions?.instructions && (
  <Box sx={{ mb: 3, maxWidth: "100%", overflow: "hidden" }}>
    <Typography sx={{ ...TYPOGRAPHY.H4, color: "#212121", mb: 1 }}>
      Specific Instructions:
    </Typography>

    <Box sx={{ mt: 1 }}>
      {Array.isArray(project.specificInstructions.instructions)
        ? project.specificInstructions.instructions.map((instruction, idx) => {
            const instructionText =
              typeof instruction === "object"
                ? instruction.text ||
                  instruction.instructionText ||
                  JSON.stringify(instruction)
                : instruction;

            const replies =
              typeof instruction === "object"
                ? instruction.replies || instruction.answers || []
                : [];

            return (
              <Box key={idx} sx={{ mb: 2 }}>
                {/* Instruction */}
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_STANDARD,
                    color: "#212121",
                    mb: 0.5,
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                >
                  {idx + 1}. {instructionText}
                </Typography>

                {/* Replies */}
                {replies.map((reply, replyIdx) => (
                  <Typography
                    key={replyIdx}
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.PRIMARY,
                      pl: 2, // optional indent
                      maxWidth: "100%",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {reply.content ||
                      reply.answer ||
                      reply.text ||
                      "Answer goes here"}
                  </Typography>
                ))}
              </Box>
            );
          })
        : (
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: "#000",
              maxWidth: "100%",
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            1. {project.specificInstructions.instructions}
          </Typography>
        )}
    </Box>
  </Box>
)}


                        {/* Checklist Section - Conditionally show template or filled data */}
                        {renderChecklistSection()}

                        {/* Other Observations from Checklist Data - Only show for VISITED or beyond */}
                        {["VISITED", "SIGNPENDING", "FINALREPORT"].includes(project.status) && renderOtherObservations()}

                        {/* Photos from Checklist Data - Simple Gallery - Only show for VISITED or beyond */}
                        {["VISITED", "SIGNPENDING", "FINALREPORT"].includes(project.status) && checklistData?.photos && (
                            <SimplePhotoGallery photos={checklistData.photos} />
                        )}

                        {/* Admin Remarks from Project Data */}
                        {renderAdminRemarks()}

                        {/* Assign Engineer Modal */}
                        {project && (hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                            <AssignEngineerModal
                                open={assignModalOpen}
                                onClose={() => setAssignModalOpen(false)}
                                project={project}
                                onSuccess={(updatedProject) => {
                                    setProject(updatedProject);
                                    setAssignModalOpen(false);
                                    fetchProjectAndRelatedData();
                                }}
                            />
                        )}

                        {/* Drawing Viewer Modal */}
                        <DrawingViewerModal
                            open={drawingModalOpen}
                            onClose={handleCloseDrawing}
                            drawing={selectedDrawing}
                        />

                        {/* Signatures Section - Only show for VISITED or beyond */}
                        {["VISITED", "SIGNPENDING", "FINALREPORT"].includes(project.status) && renderSignatures()}

                        {/* Thank You Text */}
                      {(hasRole("CLIENT")) && generalContent.thankYou?.isActive && generalContent.thankYou?.content && (
    <Box sx={{ textAlign: "center", mt: 4, mb: 3 }}>
        <Box sx={{ 
            display: 'inline-block',
            maxWidth: '80%',
            textAlign: 'center'
        }}>
            <MarkdownRenderer 
                content={generalContent.thankYou.content}
                sx={{ 
                    '& p': { 
                        color: COLORS.PRIMARY,
                        fontStyle: 'italic',
                        textAlign: 'center'
                    }
                }}
            />
        </Box>
    </Box>
)}

{/* Terms & Conditions */}
{/* Terms & Conditions */}
{(hasRole("CLIENT")) && generalContent.termsConditions?.isActive && generalContent.termsConditions?.content && (
    <Box sx={{ mt: 4, mb: 3, pageBreakBefore: "always" }}>
        <Typography sx={{ ...TYPOGRAPHY.H4, color: "#000", mb: 2 }}>
            {generalContent.termsConditions.title || "Terms & Conditions"}
        </Typography>
        <Box sx={{ pl: 2, display: 'flex', gap: 4 }}>
            {(() => {
                const content = generalContent.termsConditions.content;
                
                // Split content into two roughly equal parts
                const paragraphs = content.split('\n\n');
                const midPoint = Math.ceil(paragraphs.length / 2);
                
                const firstColumnContent = paragraphs.slice(0, midPoint).join('\n\n');
                const secondColumnContent = paragraphs.slice(midPoint).join('\n\n');
                
                return (
                    <>
                        {/* First Column */}
                        <Box sx={{ flex: 1 }}>
                            <MarkdownRenderer 
                                content={firstColumnContent}
                            />
                        </Box>
                        
                        {/* Second Column - Continuation */}
                        <Box sx={{ flex: 1 }}>
                            <MarkdownRenderer 
                                content={secondColumnContent}
                            />
                        </Box>
                    </>
                );
            })()}
        </Box>
    </Box>
)}                 </>
                )}
            </Box>
        </>
    );
};

export default ClientDetailsSidebar;